"use client";

import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Image from "next/image";
import LottiePlayer from "@/components/shared/LottiePlayer";
import TrackerAnnouncementSection from "@/components/TrackerAnnouncementSection";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

const features = [
  {
    number: "01",
    kicker: "Dream-On-Tool",
    title: "Gib deine Noten ein. Wir rechnen für dich.",
    description:
      "Trage einfach deine Werte ein: Abiturnote, TMS, HamNat, PhaST, BaPsy sowie außerschulische Aktivitäten. ncrechner berechnet in Echtzeit, an wie vielen Universitäten eine Zulassung wahrscheinlich wäre: von einer kostenlosen Prognose für das nächstmögliche Semester bis zurück in die Vergangenheit zum Sommersemester 2021.",
    lottie: "/lottie/feature-1c.json",
    image: "/images/feature-rechner.png",
    imgW: 520,
    imgH: 698,
    imageAlt: "ncrechner App: Dream-On-Tool zur Noteneingabe",
    wide: true,
  },
  {
    number: "02",
    kicker: "Deine Auswertung",
    title: "Alle Hochschulen auf einen Blick.",
    description:
      "Sieh sofort, bei welchen öffentlichen Hochschulen eine Zulassung möglich gewesen wäre: aufgeschlüsselt nach Zulassungsquote (Abi, AdH, ZEQ) und mit dem jeweiligen Punktedefizit für jede Einrichtung.",
    lottie: "/lottie/feature-2d.json",
    image: "/images/feature-auswertung.png",
    imgW: 520,
    imgH: 572,
    imageAlt: "ncrechner App: Auswertung aller Hochschulen",
    wide: true,
  },
  {
    number: "03",
    kicker: "Detail-Analyse & Prognose",
    title: "Analysiere deine Chancen im Detail.",
    description:
      "Schau dir für jede Hochschule genau an, wie deine Studienplatzchancen in den vergangenen Semestern standen: pro Quote, mit Grenzwerten und Studienplatzzahlen. Dazu erhältst du eine kostenlose Prognose für das kommende Semester.",
    image: "/images/feature-detailanalyse.png",
    imgW: 340,
    imgH: 704,
    imageAlt: "ncrechner App: Detail-Analyse mit Prognose für die Berlin Charité",
  },
  {
    number: "04",
    kicker: "Community",
    title: "Tausch dich mit tausenden Bewerbern aus.",
    description:
      "Im Community-Chat kommen alle Nutzer an einem Ort zusammen: Diskutiere deine Zulassungschancen, teile deine Ergebnisse und tausch dich über Quoten, Tests und das Bewerbungsverfahren aus. Direkt in der App.",
    image: "/images/feature-community.png",
    imgW: 340,
    imgH: 704,
    imageAlt: "ncrechner App: Community-Chat mit tausenden Mitgliedern",
  },
];

type FeatureData = {
  number: string;
  kicker: string;
  title: string;
  description: string;
  image: string;
  imgW: number;
  imgH: number;
  imageAlt: string;
  lottie?: string;
  lottieRounded?: boolean;
  wide?: boolean;
};

function FeatureRow({
  number,
  kicker,
  title,
  description,
  image,
  imageAlt,
  imgW,
  imgH,
  lottie,
  lottieRounded,
  reverse,
}: FeatureData & { reverse: boolean }) {
  const rowRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: rowRef,
    offset: ["start end", "end start"],
  });
  const phoneY = useTransform(scrollYProgress, [0, 1], [28, -28]);

  return (
    <div ref={rowRef} className="relative border-t border-line pt-14 lg:pt-16">
      <div
        className={`grid lg:grid-cols-12 gap-12 lg:gap-16 items-center ${
          reverse ? "lg:[&>*:first-child]:order-2 lg:[&>*:last-child]:order-1" : ""
        }`}
      >
        {/* Text */}
        <motion.div
          variants={stagger(0.09)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="lg:col-span-6 flex flex-col gap-6 items-center text-center lg:items-start lg:text-left"
        >
          <motion.p
            variants={fadeUp}
            className="flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-accent"
          >
            <span className="tnum text-ink-faint">{number}</span>
            <span className="w-4 h-px bg-line" aria-hidden="true" />
            {kicker}
          </motion.p>

          <motion.h3
            variants={fadeUp}
            className="font-display font-bold text-3xl sm:text-4xl tracking-tight leading-[1.1] text-ink max-w-md"
          >
            {title}
          </motion.h3>

          <motion.p variants={fadeUp} className="text-ink-soft leading-relaxed text-lg max-w-xl">
            {description}
          </motion.p>

          <motion.p variants={fadeUp} className="flex items-center gap-2 text-sm text-ink-faint">
            <span className="w-1.5 h-1.5 rounded-full bg-mint" aria-hidden="true" />
            In der App enthalten
          </motion.p>
        </motion.div>

        {/* Mockup: uniform height, aspect ratio preserved, entrance on view */}
        <motion.div
          initial={{ opacity: 0, y: 48, scale: 0.94 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: "-90px" }}
          transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-6 flex justify-center"
        >
          <motion.div style={{ y: phoneY }}>
            {lottie ? (
              <div className={lottieRounded ? "rounded-[3rem] overflow-hidden" : ""}>
                <LottiePlayer
                  src={lottie}
                  className="h-[340px] sm:h-[420px] lg:h-[480px] w-auto"
                />
              </div>
            ) : (
              <Image
                src={image}
                alt={imageAlt}
                width={imgW}
                height={imgH}
                className="h-[340px] sm:h-[420px] lg:h-[480px] w-auto drop-shadow-xl"
              />
            )}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

export default function FeaturesSection() {
  return (
    <section id="features" className="cv-auto relative bg-paper py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <motion.div
          variants={stagger(0.09)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid lg:grid-cols-12 gap-6 items-center lg:items-end mb-16 lg:mb-20 text-center lg:text-left"
        >
          <div className="lg:col-span-8 flex flex-col gap-5 items-center lg:items-start">
            <motion.p
              variants={fadeUp}
              className="flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-ink-faint"
            >
              <span className="w-1.5 h-1.5 bg-accent shrink-0" aria-hidden="true" />
              Funktionen
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-[1.1] text-ink"
            >
              Ein Werkzeug.
              <br />
              Ein Ziel: dein Studienplatz.
            </motion.h2>
          </div>
          <motion.p
            variants={fadeUp}
            className="lg:col-span-4 text-ink-faint text-sm leading-relaxed text-center lg:text-right"
          >
            Alle Funktionen kostenlos in der App.
            <br />
            Für iOS und Android.
          </motion.p>
        </motion.div>

        {/* Aufrück-Tracker-Ankündigung direkt unter dem Header */}
        <div className="mb-20 lg:mb-24">
          <TrackerAnnouncementSection />
        </div>

        {/* Zwischenüberschrift vor den Funktions-Rows */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewportOnce}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="border-t border-line pt-14 lg:pt-16 mb-16 lg:mb-20 flex justify-center lg:justify-start"
        >
          {/* max-w hält die Zeile zweizeilig, sonst wirkt sie auf Desktop verloren */}
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-[1.1] text-ink text-center lg:text-left max-w-2xl">
            So berechnest du die Chancen auf deine Zulassung
          </h2>
        </motion.div>

        {/* Rows – erste Row ohne eigenen Divider, der sitzt jetzt über der Zwischenüberschrift */}
        <div className="flex flex-col gap-20 lg:gap-24 [&>div:first-child]:border-t-0 [&>div:first-child]:pt-0">
          {features.map((feature, i) => (
            <FeatureRow key={feature.number} {...feature} reverse={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
}
